import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { getProductTypeDisplayName, getProductTypeIcon } from './summary/OrderSummaryGrid';

import { CartLineItem } from 'common/api/e-comm/models/CartLineItem';
import { monetizeWithDecimal } from 'common/utils/strings';
import { createStyles } from 'utils/createStyle';
import { useRootSelector } from 'rootStore';
import {
    getPaymentDisplayName,
    getPaymentInfo,
    getProductName,
    getWalletCardDisplayName,
} from 'features/order-summary/utils';
import { calculatePaymentFee } from 'features/order-summary/utils/useCartDetails';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { ShipToHome } from 'components/custom-icons';
import { useCardsFromWallet } from 'hooks/useCardsFromWallet';
import { GiftCard, EGiftCard } from 'common/api/e-comm/models/Card';
import { FeatureFlag } from 'components/feature-flag';

const styles = createStyles({
    container: 'p-8  max-w-lg',
    itemsContainer: ['border border-solid border-grey-3', { borderRadius: '10px' }],
    header: 'pt-2',
    headerShipToHome: [
        'duration-300 border-solid border-b border-grey-3',
        {
            background:
                'linear-gradient(180deg, rgba(241, 90, 41, 0.1) 0%, rgba(230, 18, 106, 0.1) 42.35%, rgba(126, 63, 151, 0.1) 88.48%)',
        },
    ],
    bigBtn: [
        'border border-brand-accent bg-white px-8 py-1 w-full',
        {
            borderRadius: '10px',
            boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
    ],
    bigBtnUnselected: [
        'border border-grey-2 bg-white/75 px-8 py-1 text-grey-2 w-full',
        { borderRadius: '10px' },
    ],
    shippingOptionButtons: [
        'block lg:grid gap-2 py-4 px-4',
        {
            gridTemplateColumns: '1fr 1fr',
        },
    ],
    summaryGroupItems: [
        'grid w-full',
        {
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
        },
    ],
    sendToHomeSummaryGroupItems: 'px-8 pb-4 lg:px-0 lg:pb-0 lg:pl-4 col-span-3',
    productTypeSeparator: 'py-4 px-8',
    column: 'text-xs text-center text-brand-dark font-bold border-b border-solid border-grey-3 pb-1',
    columns: {
        backgroundColor: '#F7F7F7',
        borderRadius: '10px 10px 0 0',
    },
    footer: {
        backgroundColor: '#F7F7F7',
        borderRadius: '0 0 10px 10px',
    },
    cell: 'border-b border-solid border-grey-3 py-2',
});

export const renderItems = (
    items: CartLineItem[],
    loading: boolean,
    walletCards: (GiftCard | EGiftCard)[]
) =>
    items.map((item) => (
        <React.Fragment key={item.id}>
            {loading && (
                <>
                    <div className="col-span-4">
                        <Skeleton style={{ height: '4rem' }} />
                    </div>
                </>
            )}
            {!loading && (
                <>
                    <div className={classNames('text-sm truncate pl-5 pr-2', styles.cell)}>
                        <div className="flex text-sm text-brand-dark font-bold align-middle truncate relative">
                            <span className="flex items-center float-left mr-1">
                                {getProductTypeIcon(item.product.type)}
                            </span>
                            <span className="inline-block align-text-bottom font-bold truncate w-full">
                                {getProductName(item)}
                            </span>
                        </div>
                        <div className="text-xs text-grey-1 inline-block mt-1 truncate w-full">
                            {getProductTypeDisplayName(item.product.type)}{' '}
                            {item.product.isShipToHomeEligible && (
                                <ShipToHome
                                    height="0.6rem"
                                    className="text-grey-2 inline-block ml-1"
                                />
                            )}
                            <FeatureFlag flagKey="enable_custom_card_names" fallback={true}>
                                {' '}
                                {getWalletCardDisplayName(item, walletCards)
                                    ? ` - ${getWalletCardDisplayName(item, walletCards)}`
                                    : ''}
                            </FeatureFlag>
                        </div>
                    </div>
                    <div
                        className={classNames(
                            'text-sm flex items-center justify-center text-center text-grey-1',
                            styles.cell
                        )}
                    >
                        {monetizeWithDecimal(item.price)}
                    </div>
                    <div
                        className={classNames(
                            'text-sm flex items-center justify-center text-center text-grey-1',
                            styles.cell
                        )}
                    >
                        {item.quantity}
                    </div>
                    <div
                        className={classNames(
                            'text-sm flex items-center justify-center text-center font-bold text-brand-dark',
                            styles.cell
                        )}
                    >
                        {monetizeWithDecimal(item.subtotal)}
                    </div>
                </>
            )}
        </React.Fragment>
    ));

export const OrderSummaryDetails = () => {
    const { cart, cartAsyncState } = useRootSelector((s) => s.store.cart);
    const items = cart?.lineItems || [];
    const loading = cartAsyncState.loading;
    const { cartInfoDetails } = useContext(OrderScreenContext);

    const walletCards = useCardsFromWallet();

    const {
        total,
        payment: { payment, paymentFee, paymentAccount },
        shipping: { shippingFee, selectedShipping },
        shipToHomeItems,
        shipToCoordinator,
    } = cartInfoDetails;

    const paymentTypeDisplayName = getPaymentDisplayName(paymentFee?.paymentType);
    const paymentFeeAmount = cart
        ? calculatePaymentFee(cart.subtotal + shippingFee, paymentFee)
        : 0;

    const renderPaymentMethod = () => {
        return (
            paymentFeeAmount > 0 && (
                <>
                    <div className="mt-1 px-5 text-xs text-grey-1 col-span-2">
                        {paymentTypeDisplayName}
                    </div>

                    <div className="mt-1 px-5 text-sm text-grey-1 text-center" />

                    <div className="mt-1 px-5 text-sm text-grey-1 text-center">
                        {monetizeWithDecimal(paymentFeeAmount)}
                    </div>
                </>
            )
        );
    };

    const renderShippingMethod = () => {
        return (
            <>
                <div className="mt-1 px-5 text-xs text-xs text-grey-1 col-span-2">
                    Shipping &amp; Handling
                </div>
                <div className="mt-1 px-5 text-sm text-grey-1 text-center" />
                <div className="mt-1 px-5 text-sm text-grey-1 text-center">
                    {selectedShipping ? monetizeWithDecimal(selectedShipping.cost) : '-'}
                </div>
            </>
        );
    };

    const paymentInfo = useMemo(() => getPaymentInfo(paymentAccount), [paymentAccount]);

    let totalShipToCoordinatorItems = 0;
    let totallShipToHomeItems = 0;

    for (let i = 0; i < shipToHomeItems.length; ++i) {
        totallShipToHomeItems += shipToHomeItems[i].quantity || 1;
    }

    for (let i = 0; i < shipToCoordinator.length; ++i) {
        totalShipToCoordinatorItems += shipToCoordinator[i].quantity || 1;
    }

    const renderSummaryDetails = () => {
        return (
            <div className={classNames(styles.itemsContainer, 'mt-8 px-5 py-4')}>
                <div>
                    {(shipToHomeItems.length > 0 || shipToCoordinator.length > 0) && (
                        <div className="flex items-center border-b border-solid border-grey-3 py-2">
                            <div className="text-sm pr-4 text-grey-1">Ship to</div>
                            <ul className="text-sm list-disc ml-6 text-brand">
                                {totallShipToHomeItems > 0 && (
                                    <li>
                                        <div>Ship to home: {totallShipToHomeItems} items</div>
                                    </li>
                                )}
                                {totalShipToCoordinatorItems > 0 && (
                                    <li>
                                        <div>
                                            Ship to coordinator: {totalShipToCoordinatorItems} items
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}
                    <div className="flex items-center py-2 text-sm">
                        <div className="pr-4 text-grey-1">Payment</div>
                        <div data-recording-sensitive className="text-brand">
                            {paymentInfo}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className="text-xl text-brand-dark mb-2 font-bold">Cart</div>

            {loading && (
                <>
                    <div className="col-span-4">
                        <Skeleton style={{ height: '4rem' }} />
                    </div>
                </>
            )}
            {!loading && (
                <>
                    <div>
                        <div className={styles.itemsContainer}>
                            <div className={classNames(styles.summaryGroupItems, 'p-4 grid')}>
                                <div
                                    className={styles.column}
                                    style={{ textAlign: 'left', paddingLeft: '1.6rem' }}
                                >
                                    Product
                                </div>

                                <div className={styles.column}>Value</div>

                                <div className={styles.column}>Quantity</div>

                                <div className={styles.column}>Amount</div>

                                {renderItems(items, loading, walletCards)}

                                <div className="mt-3 px-5 text-xs text-grey-1 col-span-2">
                                    Subtotal
                                </div>

                                <div className="mt-3 px-5 text-sm text-grey-1 text-center">
                                    {cart?.productCount}
                                </div>

                                <div className="mt-3 px-5 text-sm text-brand-dark text-center font-bold">
                                    {monetizeWithDecimal(cart?.subtotal || 0)}
                                </div>

                                {renderPaymentMethod()}

                                {renderShippingMethod()}

                                <div className="mt-3 text-sm border-b border-solid border-grey-3 col-span-4" />

                                <div className="mt-2 text-center col-span-2" />

                                <div className="mt-2 flex items-center text-xs justify-center text-brand-dark font-bold">
                                    Total
                                </div>

                                <div className="mt-2 text-center font-bold text-brand-dark">
                                    {monetizeWithDecimal(total)}
                                </div>
                            </div>
                        </div>
                        {renderSummaryDetails()}
                    </div>
                </>
            )}
        </div>
    );
};
